import * as React from "react";
import {alpha, Box, Divider, Stack, SxProps, Theme} from "@mui/material";
import Typography from "@mui/material/Typography";
import {useTheme} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import stb_logo from "../../../assets/images/stb-logo.svg";
import cpay_logo from "../../../assets/images/cpay-logo.svg";
import visa_logo from "../../../assets/images/visa.png";
import mastercard_logo from "../../../assets/images/mastercard.png";
import diners_logo from "../../../assets/images/diners-club.png";

interface IProps {
    variant?: "footer" | "dialog"
    sx?: SxProps<Theme>
    align?: "start" | "center" | "end"
    framed?: boolean
}

const SecurePayments = ({variant = "footer", sx, align, framed = false}: IProps) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const isDialog = variant === "dialog"
    const effectiveAlign = align ?? (isDialog ? "start" : "end")

    const paymentLogoSx = {
        height: isDialog ? {xs: 18, sm: 20} : {xs: 20, sm: 22},
        width: "auto",
        objectFit: "contain"
    }
    const alignItems = effectiveAlign === "center"
        ? "center"
        : effectiveAlign === "start"
            ? "flex-start"
            : {xs: "center", md: "flex-end"} as const
    const textAlign = effectiveAlign === "center"
        ? "center"
        : effectiveAlign === "start"
            ? "left"
            : {xs: "center", md: "right"} as const
    const justifyContent = effectiveAlign === "center"
        ? "center"
        : effectiveAlign === "start"
            ? "flex-start"
            : {xs: "center", md: "flex-end"} as const

    return (
        <Stack
            spacing={isDialog ? 0.7 : 0.55}
            sx={{
                alignItems,
                textAlign,
                ...(framed ? {
                    px: {xs: 1.25, sm: 1.5},
                    py: 0.9,
                    borderRadius: 1.25,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                    backgroundColor: alpha(theme.palette.primary.main, 0.025),
                } : undefined),
                ...sx
            }}
        >
            {isDialog && !framed ? (
                <Divider sx={{
                    width: "100%",
                    borderColor: alpha(theme.palette.primary.main, 0.12),
                    mb: 0.2
                }}/>
            ) : null}
            <Typography
                variant={isDialog ? "body2" : "caption"}
                sx={{
                    color: alpha(theme.palette.primary.main, isDialog ? 0.58 : 0.68),
                    fontWeight: isDialog ? 500 : 400,
                }}
            >
                {t("secure_payments")}
            </Typography>

            <Stack
                direction="row"
                spacing={isDialog ? 1.1 : 2}
                useFlexGap
                flexWrap="wrap"
                justifyContent={justifyContent}
                alignItems="center"
            >
                <Box sx={(currentTheme) => ({
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: isDialog ? 0.9 : 1,
                    py: isDialog ? 0.35 : 0.4,
                    borderRadius: isDialog ? 1 : 1,
                    backgroundColor: currentTheme.palette.primary.main,
                })}>
                    <Box component="img" src={cpay_logo} alt="cPay logo"
                         sx={{height: isDialog ? {xs: 14, sm: 16} : {xs: 16, sm: 18}, width: "auto"}}/>
                </Box>
                <Box component="img" src={stb_logo} alt="STB bank logo" sx={paymentLogoSx}/>
                <Box component="img" src={visa_logo} alt="Visa logo" sx={paymentLogoSx}/>
                <Box component="img" src={mastercard_logo} alt="Mastercard logo" sx={paymentLogoSx}/>
                <Box component="img" src={diners_logo} alt="Diners logo" sx={paymentLogoSx}/>
            </Stack>
        </Stack>
    )
}

export default SecurePayments
